import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Check, Loader2 } from "lucide-react";
import { HelpLink, HelpNote, HelpPage } from "@/components/HelpPage";
import { contactMessageSchema, sendContactMessage } from "@/lib/contact.functions";

const subjects = ["Ma commande", "Livraison", "Échange / retour", "Taille / produit", "Autre"] as const;

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — Sunset Concept Store" },
      {
        name: "description",
        content:
          "Une question sur un produit, une taille, votre commande ou votre livraison ? Écrivez à l'équipe Sunset Concept Store.",
      },
      { property: "og:title", content: "Contact — Sunset Concept Store" },
      {
        property: "og:description",
        content: "Notre équipe est à votre disposition pour toute question sur votre commande ou nos produits.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sunsetconcept.store/contact" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sunsetconcept.store/contact" }],
  }),
  component: ContactPage,
});

const fieldClass =
  "h-14 w-full border border-border bg-background px-4 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-accent";

function ContactPage() {
  const send = useServerFn(sendContactMessage);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const fd = new FormData(e.currentTarget);
    const parsed = contactMessageSchema.safeParse({
      fullName: String(fd.get("fullName") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      email: String(fd.get("email") ?? ""),
      subject: String(fd.get("subject") ?? ""),
      message: String(fd.get("message") ?? ""),
    });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Merci de vérifier les informations saisies.");
      return;
    }

    setSending(true);
    try {
      await send({ data: parsed.data });
      setSent(true);
    } catch {
      setError("Envoi impossible pour le moment. Merci de réessayer dans quelques instants.");
    } finally {
      setSending(false);
    }
  }

  return (
    <HelpPage
      title="Contact"
      intro="Une question sur un produit, une taille, votre commande ou votre livraison ? Notre équipe est à votre disposition."
    >
      {sent ? (
        <section
          role="status"
          className="flex flex-col items-center border border-border bg-sand/30 px-6 py-16 text-center sm:px-12"
        >
          <span className="grid h-14 w-14 place-items-center rounded-full bg-background">
            <Check size={22} strokeWidth={1.3} className="text-accent" />
          </span>
          <h2 className="mt-8 font-serif text-2xl font-light leading-snug tracking-tight sm:text-3xl">
            Merci pour votre message.
          </h2>
          <p className="mt-4 max-w-md text-sm leading-[1.9] text-muted-foreground">
            Notre équipe vous répondra dans les meilleurs délais.
          </p>
        </section>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6" noValidate>
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="fullName" className="label-xs text-foreground">
                Nom complet
              </label>
              <input id="fullName" name="fullName" required autoComplete="name" className={`${fieldClass} mt-3`} />
            </div>
            <div>
              <label htmlFor="phone" className="label-xs text-foreground">
                Téléphone
              </label>
              <input
                id="phone"
                name="phone"
                required
                inputMode="tel"
                autoComplete="tel"
                placeholder="06 12 34 56 78"
                className={`${fieldClass} mt-3`}
              />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="email" className="label-xs text-foreground">
                E-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Facultatif"
                className={`${fieldClass} mt-3`}
              />
            </div>
            <div>
              <label htmlFor="subject" className="label-xs text-foreground">
                Sujet
              </label>
              <select id="subject" name="subject" required defaultValue={subjects[0]} className={`${fieldClass} mt-3`}>
                {subjects.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="message" className="label-xs text-foreground">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={6}
              className="mt-3 w-full resize-y border border-border bg-background p-4 text-sm leading-relaxed outline-none transition-colors placeholder:text-muted-foreground focus:border-accent"
              placeholder="Décrivez votre demande…"
            />
          </div>

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <button type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-40 sm:w-auto">
            {sending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                Envoi…
              </span>
            ) : (
              "Envoyer"
            )}
          </button>
        </form>
      )}

      <HelpNote>
        Avant de nous écrire, vous trouverez peut-être votre réponse dans la <HelpLink to="/faq">FAQ</HelpLink>, sur la
        page <HelpLink to="/livraison">Livraison</HelpLink> ou dans notre politique{" "}
        <HelpLink to="/echanges-retours">Échanges &amp; retours</HelpLink>.
      </HelpNote>
    </HelpPage>
  );
}
